import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CurrentDealProvider } from "@/context/CurrentDealContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";

import Login from "@/pages/Login";
import TrocarSenha from "@/pages/TrocarSenha";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Clientes from "@/pages/Clientes";
import ClienteForm from "@/pages/ClienteForm";
import ClienteDetalhe from "@/pages/ClienteDetalhe";
import Pipeline from "@/pages/Pipeline";
import PipelineEncerradas from "@/pages/PipelineEncerradas";
import DealForm from "@/pages/DealForm";
import DealDetalhe from "@/pages/DealDetalhe";
import Atividades from "@/pages/Atividades";
import Atencao from "@/pages/Atencao";
import Previsao from "@/pages/Previsao";
import RelatoriosHub from "@/pages/RelatoriosHub";
import RelatoriosRedirect from "@/components/RelatoriosRedirect";
import Carteira from "@/pages/Carteira";
import Operacao from "@/pages/Operacao";
import Equipe from "@/pages/Equipe";
import SemInteracao from "@/pages/SemInteracao";
import CotasLote from "@/pages/CotasLote";
import AuditoriaCadastro from "@/pages/AuditoriaCadastro";
import MembroDetalhe from "@/pages/MembroDetalhe";
import Potenciais from "@/pages/Potenciais";
import Prospectar from "@/pages/Prospectar";
import Captacao from "@/pages/Captacao";
import Captar from "@/pages/Captar";
import Ti from "@/pages/Ti";
import ImportarClientesAtivos from "@/pages/ImportarClientesAtivos";
import ImportarCarteiraSankhya from "@/pages/ImportarCarteiraSankhya";
import AjustesSankhya from "@/pages/AjustesSankhya";
import FerramentasAdmin from "@/pages/FerramentasAdmin";
import Faturamento from "@/pages/Faturamento";
import Busca from "@/pages/Busca";
import Perfil from "@/pages/Perfil";
import Configuracoes from "@/pages/Configuracoes";
import NotFound from "@/pages/NotFound";
import Foco from "@/pages/Foco";
import CustoPorUso from "@/pages/CustoPorUso";
import Logistica from "@/pages/Logistica";
import TVLogistica from "@/pages/TVLogistica";
import FaturamentoVendedor from "@/pages/FaturamentoVendedor";
import Solicitacao from "@/pages/Solicitacao";
import PadraoVisual from "@/pages/PadraoVisual";
import PainelUnificado from "@/pages/PainelUnificado";
import { useAuth } from "@/context/AuthContext";
import CanalClienteQ from "@/pages/CanalClienteQ";
import Treinamentos from "@/pages/Treinamentos";
import DesafioProjetor from "@/pages/DesafioProjetor";
import DesafioParticipante from "@/pages/DesafioParticipante";
import TreinamentoHospitalar from "@/pages/TreinamentoHospitalar";

// PAINEL-1.2 — painel UNIFICADO em "/" para admin, diretor_geral, director e
// manager. Vendedor continua no Dashboard. Rota antiga `/painel-lideranca`
// redireciona para "/" para evitar dois painéis. `?legacy=1` foi removido.
function HomeByRole() {
  const { user } = useAuth();
  const r = user?.role;
  if (r && ["admin", "diretor_geral", "director", "manager"].includes(r)) {
    return <PainelUnificado />;
  }
  return <Dashboard />;
}

// Lote 3 CRM-006 — /admin/auditoria-cadastro só para Administrador;
// os demais são redirecionados para o Início.
function AdminOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

const shell = (el, errorMessage) => (
  <ProtectedRoute>
    <CurrentDealProvider>
      <AppShell>
        <ErrorBoundary message={errorMessage}>{el}</ErrorBoundary>
      </AppShell>
    </CurrentDealProvider>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/q/:token" element={<CanalClienteQ />} />
            <Route path="/desafio" element={<DesafioParticipante />} />
            <Route path="/desafio/:pin" element={<DesafioParticipante />} />
            <Route path="/desafio-projetor/:pin" element={<ProtectedRoute><DesafioProjetor /></ProtectedRoute>} />
            <Route path="/treinamentos" element={shell(<Treinamentos />)} />
            <Route path="/painel-lideranca" element={<Navigate to="/" replace />} />
            <Route path="/treinamento/hospitalar/:apid" element={<ProtectedRoute><TreinamentoHospitalar /></ProtectedRoute>} />
            <Route path="/trocar-senha" element={<ProtectedRoute><TrocarSenha /></ProtectedRoute>} />

            <Route path="/" element={shell(<HomeByRole />)} />
            <Route path="/clientes" element={shell(<Clientes />)} />
            <Route path="/cliente/novo" element={<Navigate to="/prospect/novo" replace />} />
            <Route path="/prospect/novo" element={shell(<ClienteForm />)} />
            <Route path="/cliente/:id/editar" element={shell(<ClienteForm />)} />
            <Route path="/cliente/:id" element={shell(<ClienteDetalhe />)} />
            <Route path="/pipeline" element={shell(<Pipeline />)} />
            <Route path="/pipeline/encerradas" element={shell(<PipelineEncerradas />)} />
            <Route path="/deal/novo" element={shell(<DealForm />)} />
            <Route path="/deal/:id/editar" element={shell(<DealForm />)} />
            <Route path="/deal/:id" element={shell(<DealDetalhe />, "Não foi possível abrir esta oportunidade")} />
            <Route path="/atividades" element={shell(<Atividades />)} />
            <Route path="/atencao" element={shell(<Atencao />)} />
            <Route path="/foco/:tipo" element={shell(<Foco />)} />
            <Route path="/previsao" element={shell(<Previsao />)} />
            <Route path="/relatorios" element={shell(<RelatoriosHub />)} />
            <Route path="/faturamento" element={shell(<Faturamento />)} />
            <Route path="/faturamento/vendedor/:id" element={shell(<FaturamentoVendedor />)} />
            <Route path="/solicitacao/:id" element={shell(<Solicitacao />)} />
            <Route path="/desempenho" element={shell(<RelatoriosRedirect tab="desempenho" />)} />
            <Route path="/resumo-executivo" element={shell(<RelatoriosRedirect tab="resumo" />)} />
            <Route path="/relatorio-gerencial" element={shell(<RelatoriosRedirect tab="gerencial" />)} />
            <Route path="/carteira" element={shell(<Carteira />)} />
            <Route path="/operacao" element={shell(<Operacao />)} />
            <Route path="/equipe" element={shell(<Equipe />)} />
            <Route path="/equipe/cotas-lote" element={shell(<CotasLote />)} />
            <Route path="/equipe/sem-interacao" element={shell(<SemInteracao />)} />
            <Route path="/admin/auditoria-cadastro" element={shell(<AdminOnlyRoute><AuditoriaCadastro /></AdminOnlyRoute>)} />
            <Route path="/admin/padrao-visual" element={shell(<PadraoVisual />)} />
            <Route path="/membro/:id" element={shell(<MembroDetalhe />)} />
            <Route path="/potenciais" element={shell(<Potenciais />)} />
            <Route path="/prospectar" element={shell(<Prospectar />)} />
            <Route path="/captacao" element={shell(<Captacao />)} />
            <Route path="/captar" element={shell(<Captar />)} />
            <Route path="/ti" element={shell(<Ti />)} />
            <Route path="/importar-clientes-ativos" element={shell(<ImportarClientesAtivos />)} />
            <Route path="/importar-carteira-sankhya" element={shell(<ImportarCarteiraSankhya />)} />
            <Route path="/ajustes/sankhya" element={shell(<AjustesSankhya />)} />
            <Route path="/ajustes/ferramentas-admin" element={shell(<FerramentasAdmin />)} />
            <Route path="/busca" element={<Navigate to="/" replace />} />
            <Route path="/perfil" element={shell(<Perfil />)} />
            <Route path="/configuracoes" element={shell(<Configuracoes />)} />
            <Route path="/custo-por-uso" element={<ProtectedRoute><CustoPorUso /></ProtectedRoute>} />
            <Route path="/logistica" element={<ProtectedRoute><Logistica /></ProtectedRoute>} />
            <Route path="/tv" element={<ProtectedRoute><TVLogistica /></ProtectedRoute>} />
            <Route path="*" element={shell(<NotFound />)} />
          </Routes>
        </BrowserRouter>
        {/* LOTE UX-1 C1 — duas instâncias de Toaster. Cada aparece em
            UM breakpoint só; a outra fica escondida via CSS para não
            duplicar o toast. Mobile vai na parte de baixo, acima da
            barra de navegação; respeita safe-area do iPhone. */}
        <div className="hidden md:block"><Toaster position="top-right" richColors /></div>
        <div className="md:hidden"><Toaster position="bottom-center" richColors offset={72} mobileOffset={{ bottom: "calc(76px + env(safe-area-inset-bottom))" }} /></div>
      </AuthProvider>
    </div>
  );
}

export default App;
